import { Query, skipToken, useQuery } from "@tanstack/react-query";

import { getRanaApiV1 } from "@nens/rana/api-v1";
import { ApiError } from "@nens/react-query-utils";
import {
  FileStateEnum,
  PaginatedRasterList,
  ThreediModel,
  getHCCApi,
} from "@nens/threedi/openapi-client";

import { modelIsBeingGenerated } from "@/utils/model-is-being-generated";

const staticFileStates: FileStateEnum[] = [
  FileStateEnum.UPLOADED,
  FileStateEnum.ERROR,
  FileStateEnum.REMOVED,
];
const getHccRasterListQuery = (model: ThreediModel | null | undefined) => {
  const modelId = model?.id;
  const isBeingGenerated = modelIsBeingGenerated(model);
  return getRanaApiV1().getQueryOptions<PaginatedRasterList>({
    // The generation state is part of the key so that the list is fetched once more when the model
    // finishes generating. The interval below only looks at the last fetched data, which can be
    // empty or outdated at that moment and would then stop polling too early.
    queryKey: ["hcc-raster", modelId, isBeingGenerated],
    queryFn: modelId
      ? () =>
          getHCCApi().threedimodelsRastersList({
            threedimodelPk: modelId,
          })
      : skipToken,
    // Keep showing the previous list of the same model (instead of a loading state) while the
    // new key is fetched. getQueryOptions prefixes the key with the api name and tenant id.
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey.some((key) => key === modelId) ? previousData : undefined,
    // Rasters keep being processed for a while after the model itself is marked as done, so deciding
    // on the model state alone can stop polling before the final raster list was fetched. Keep
    // polling until every raster file is processed (or failed) as well.
    refetchInterval: (query: Query<PaginatedRasterList, ApiError, PaginatedRasterList>) => {
      const hasPendingRaster = query.state.data?.results.some(
        (raster) =>
          raster.file?.state !== undefined && !staticFileStates.includes(raster.file?.state)
      );
      if (isBeingGenerated) return 10_000;
      if (hasPendingRaster) return 2_500;
      return false;
    },
  });
};

export const useHccRasterListQuery = (model: ThreediModel | null | undefined) => {
  return useQuery(getHccRasterListQuery(model));
};
